import { gql } from 'graphql-request'
import { Pair } from '@pancakeswap/sdk'
import { ChainId } from '@pancakeswap/chains'
import { useActiveChainId } from 'hooks/useActiveChainId'
import useSWRImmutable from 'swr/immutable'
import { getDeltaTimestamps } from 'utils/getDeltaTimestamps'
import { getBlocksV2FromTimestamps, getBlocksFromTimestamps } from 'utils/getBlocksFromTimestamps'
import { getChangeForPeriod } from 'utils/getChangeForPeriod'
import { SLOW_INTERVAL } from 'config/constants'
import { getLpFeesAndApr } from 'utils/getLpFeesAndApr'
import { LP_HOLDERS_FEE, WEEKS_IN_YEAR } from 'config/constants/info'
import { getMultiChainQueryEndPointWithStableSwap, MultiChainName, multiChainQueryMainToken, multiChainName, multiChainBlockTime, multiChainStartBlock } from '../info/constant'

interface PoolReserveVolume {
  reserveUSD: string
  volumeUSD: string
  untrackedVolumeUSD: string
}

interface PoolReserveVolumeResponse {
  now: PoolReserveVolume[]
  oneDayAgo: PoolReserveVolume[]
  twoDaysAgo: PoolReserveVolume[]
  oneWeekAgo: PoolReserveVolume[]
  twoWeeksAgo: PoolReserveVolume[]
}

export const useLPApr = (poolAddress?: string, isStableSwap: boolean = false) => {
  const { chainId } = useActiveChainId()
  const { data: poolData } = useSWRImmutable(
    poolAddress && ['LP7dApr', poolAddress],
    async () => {
      const timestampsArray = getDeltaTimestamps()
      const blocks = await getBlocksFromTimestamps(timestampsArray, 'desc', multiChainBlockTime[chainId], multiChainName[chainId])
      const [, , block7d] = blocks ?? []
      const { error, data } = await fetchPoolVolumeAndReserveData(
        block7d.number,
        poolAddress.toLowerCase(),
        multiChainName[chainId],
        isStableSwap
      )
      if (error) return null
      const currentReserveUSD = parseFloat(data?.now[0]?.reserveUSD)
      const current = parseFloat(data?.now[0]?.volumeUSD ?? '0') + parseFloat(data?.now[0]?.untrackedVolumeUSD ?? '0')
      const week = parseFloat(data?.oneWeekAgo[0]?.volumeUSD ?? '0') + parseFloat(data?.oneWeekAgo[0]?.untrackedVolumeUSD ?? '0')
      const [volumeUSDWeek] = getChangeForPeriod(current, week)
      const liquidityUSD = currentReserveUSD || 0
      const {lpApr7d} = getLpFeesAndApr(current, volumeUSDWeek, liquidityUSD)
      // const lpApr7d = liquidityUSD > 0 ? (volumeUSDWeek * LP_HOLDERS_FEE * WEEKS_IN_YEAR * 100) / liquidityUSD : 0
      return lpApr7d ? { lpApr7d } : null
    },
    {
      refreshInterval: SLOW_INTERVAL,
    },
  )

  return poolData
}

export const useLPAprWithAddress = (farmAddress: string, chainId: number) => {
  const { data: poolData } = useSWRImmutable(
    farmAddress && ['LP7dAprWithFarmAddress', farmAddress],
    async () => {
      const timestampsArray = getDeltaTimestamps()
      const blocks = await getBlocksV2FromTimestamps(timestampsArray, 'desc', multiChainBlockTime[chainId], multiChainName[chainId])
      const [, , block7d] = blocks ?? []
      const block7dNumber = block7d?.number ?? null;
      const { error, data } = await fetchPoolVolumeAndReserveData(
        block7dNumber,
        farmAddress.toLowerCase(),
        multiChainName[chainId]
      )
    
      if (error) return null
      const currentReserveUSD = parseFloat(data?.now[0]?.reserveUSD)
      const current = parseFloat(data?.now[0]?.volumeUSD ?? '0') + parseFloat(data?.now[0]?.untrackedVolumeUSD ?? '0')
      const week = parseFloat(data?.oneWeekAgo[0]?.volumeUSD ?? '0') + parseFloat(data?.oneWeekAgo[0]?.untrackedVolumeUSD ?? '0')
      const [volumeUSDWeek] = getChangeForPeriod(current, week)
      const liquidityUSD = currentReserveUSD || 0
      const {lpApr7d} = getLpFeesAndApr(current, volumeUSDWeek, liquidityUSD)
      // const lpApr7d = liquidityUSD > 0 ? (volumeUSDWeek * LP_HOLDERS_FEE * WEEKS_IN_YEAR * 100) / liquidityUSD : 0
      return lpApr7d ? { lpApr7d } : null
    },
    {
      refreshInterval: SLOW_INTERVAL,
    },
  )
  return poolData
}

const fetchPoolVolumeAndReserveData = async (
  block7d: number,
  poolAddress: string,
  chainName: MultiChainName,
  isStableSwap: boolean = false
) => {
  try {
    const query = gql`
      query pools {
        now: ${POOL_AT_BLOCK(chainName, null, poolAddress)}
        oneWeekAgo: ${POOL_AT_BLOCK(chainName, block7d, poolAddress)}
      }
    `

    const data = await getMultiChainQueryEndPointWithStableSwap(chainName, isStableSwap).request<PoolReserveVolumeResponse>(query)
    return { data, error: false }
  } catch (error) {
    console.error('Failed to fetch pool data', error)
    return { error: true }
  }
}
const POOL_AT_BLOCK = (chainName: MultiChainName, block: number | null, pool: string) => {
  const addressesString = `["${pool}"]`
  const blockString = block ? `block: {number: ${Math.max(block, multiChainStartBlock[chainName])}}` : ``
  return `pairs(
    where: { id_in: ${addressesString} }
    ${blockString}
  ) {
    reserveUSD
    volumeUSD
    untrackedVolumeUSD
  }`
}
